/**
 * Loading a custom map into the game (`?map=<id>`).
 *
 * Three sources, in the order a person expects them:
 *   `?map=night_market`  a map shipped in public/maps/
 *   `?map=draft`         whatever the editor last saved locally
 *   `?map=<url>`         an http(s) URL, for a map a teammate has hosted
 *
 * A map that fails to load must say why on screen rather than dropping the
 * player into the built-in district and pretending nothing happened — the
 * whole point of a custom map is that you meant to play THAT one.
 */

import { validateMap } from "../sim/map/validate";
import type { MapDoc, MapIssue } from "../sim/map/types";

/** localStorage key the editor writes its working draft to. */
export const DRAFT_KEY = "streetsense_map_draft";

export interface MapLoadResult {
  doc: MapDoc | null;
  /** Everything the validator said — errors block, warnings are shown once. */
  issues: MapIssue[];
  /** Human-readable reason the map could not be used. */
  error: string | null;
}

export async function loadMap(idOrUrl: string): Promise<MapLoadResult> {
  let raw: unknown;
  try {
    raw = idOrUrl === "draft" ? loadDraft() : await fetchMap(idOrUrl);
  } catch (e) {
    return { doc: null, issues: [], error: `Could not load map "${idOrUrl}": ${message(e)}` };
  }
  if (raw === null) {
    return {
      doc: null,
      issues: [],
      error:
        idOrUrl === "draft"
          ? "No saved draft on this device — open the editor and save one first."
          : `Map "${idOrUrl}" is empty.`,
    };
  }

  const result = validateMap(raw);
  if (!result.ok) {
    const errors = result.issues.filter((i) => i.level === "error");
    const lines = errors.slice(0, 5).map((i) => `• ${i.path ? `${i.path}: ` : ""}${i.message}`);
    const more = errors.length > lines.length ? `\n…and ${errors.length - lines.length} more.` : "";
    return {
      doc: null,
      issues: result.issues,
      error: `Map "${idOrUrl}" has ${errors.length} problem(s):\n${lines.join("\n")}${more}`,
    };
  }
  return { doc: raw as MapDoc, issues: result.issues, error: null };
}

async function fetchMap(idOrUrl: string): Promise<unknown> {
  const url = /^https?:\/\//i.test(idOrUrl)
    ? idOrUrl
    : `${import.meta.env.BASE_URL}maps/${encodeURIComponent(idOrUrl)}.json`;
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as unknown;
}

function loadDraft(): unknown {
  const raw = localStorage.getItem(DRAFT_KEY);
  return raw ? (JSON.parse(raw) as unknown) : null;
}

/** Save a map as the local draft (used by the editor's Play button). */
export function saveDraft(doc: MapDoc): void {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(doc));
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Full-screen "this map will not load" panel. Deliberately plain text. */
export function showMapError(root: HTMLElement, text: string): void {
  const panel = document.createElement("div");
  panel.id = "map-error";
  panel.setAttribute("role", "alert");
  panel.textContent = text;
  const back = document.createElement("button");
  back.type = "button";
  back.textContent = "Play the district instead";
  back.addEventListener("click", () => {
    const q = new URLSearchParams(window.location.search);
    q.delete("map");
    window.location.search = q.toString();
  });
  panel.appendChild(back);
  root.appendChild(panel);
}
